import { useState, useEffect } from "react";
import { Save, RotateCcw, Plus, Trash2, ChevronDown, ChevronRight, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { translations } from "@/i18n";
import { deepMerge, setPath, diffContent } from "@/lib/content";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

const inputCls = "w-full bg-[#1A1A1A] border border-white/10 text-white p-2.5 text-sm focus:border-[#ff0033] outline-none";

const SECTION_LABELS = {
  home: "Start (Home)",
  trainer: "Unsere Trainer · Michael Wolf", trainer2: "Unsere Trainer · Christian Tobias",
  trainer3: "Unsere Trainer · Moritz Ehrismann", trainer4: "Unsere Trainer · Mikael Krantz",
  kurse: "Gruppenkurse", regelkurs: "Regelkurs", einzel: "Einzeltraining", online: "Onlinecoaching",
  golfclub: "Golfclub GC KW", trackman: "TrackMan buchen", gutschein: "Gutschein kaufen",
  kontakt: "Kontakt", impressum: "Impressum",
  nav: "Navigation (Menü)", marquee: "Laufband (Banner)", footer: "Footer",
};

// Reihenfolge im Editor exakt wie die Menüpunkte in der Navigation (danach Zusatzbereiche)
const SECTION_ORDER = [
  "home", "trainer", "trainer2", "trainer3", "trainer4", "kurse", "regelkurs", "einzel",
  "online", "golfclub", "trackman", "gutschein", "kontakt", "impressum", "nav", "marquee", "footer",
];

const KEY_LABELS = {
  kicker: "Kleiner Titel (Kicker)", title: "Titel", intro: "Einleitung", sub: "Untertitel", role: "Rolle",
  bio: "Biografie (Absätze)", quals: "Qualifikationen", qualsTitle: "Titel Qualifikationen", cta: "Button-Text",
  h1a: "Überschrift Zeile 1", h1b: "Überschrift Zeile 2", features: "Kacheln", items: "Einträge", offers: "Angebote",
  content: "Text", address: "Adresse", plans: "Preispläne", topics: "Themen", includes: "Enthalten",
  t: "Titel", d: "Beschreibung", long: "Langtext", meta: "Zusatz", name: "Name", price: "Preis", per: "Zeitraum",
  valid: "Gültigkeit", q: "Frage", a: "Antwort",
  // TrackMan
  locationFallback: "Standort (Fallback)", memberLabel: "Label Mitglied",
  guestLabel: "Label Gast", bookingTitle: "Titel Buchungsfenster", memberQuestion: "Frage Mitgliedschaft",
  consentVideo: "Einwilligung Videoüberwachung", consentKey: "Einwilligung Schlüssel-Rückgabe",
  paymentNote: "Hinweis Zahlung", doneTitle: "Bestätigung · Titel", doneText: "Bestätigung · Text", doneNote: "Bestätigung · Zugangscode-Hinweis",
  // Kurse-Seite
  emptyText: "Text: keine Kurse", calendarKicker: "Kalender · Kicker", calendarTitle: "Kalender · Titel",
  regelkursKicker: "Regelkurs-Box · Kicker", regelkursTitle: "Regelkurs-Box · Titel",
  regelkursText1: "Regelkurs-Box · Absatz 1", regelkursText2: "Regelkurs-Box · Absatz 2", regelkursCta: "Regelkurs-Box · Button",
  voucherTitle: "Gutschein-Box · Titel", voucherText: "Gutschein-Box · Text", voucherCta: "Gutschein-Box · Button",
  // Regelkurs / Gutschein
  note: "Hinweis", redeemNote: "Einlöse-Hinweis",
};

const labelFor = (key, isSection) => (isSection ? SECTION_LABELS[key] : KEY_LABELS[key]) || key;

const emptyLike = (sample) => {
  if (typeof sample === "string") return "";
  if (typeof sample === "number") return 0;
  if (typeof sample === "boolean") return false;
  if (Array.isArray(sample)) return [];
  if (sample && typeof sample === "object") {
    const o = {};
    for (const k of Object.keys(sample)) o[k] = emptyLike(sample[k]);
    return o;
  }
  return "";
};

// Plain recursive renderer (lowercase, called as a function — not a React component)
// to avoid the react-refresh self-reference stack-overflow bug.
const renderField = (fieldKey, value, path, onChange, depth) => {
  const label = labelFor(fieldKey, depth === 0);
  const key = path.join(".");

  if (value === null || value === undefined || typeof value === "string") {
    const str = String(value ?? "");
    const multiline = str.length > 55 || str.includes("\n");
    return (
      <label className="block" key={key}>
        <span className="text-neutral-500 text-[11px] uppercase tracking-wide">{label}</span>
        {multiline
          ? <textarea value={str} onChange={(e) => onChange(path, e.target.value)} rows={Math.min(8, Math.max(2, str.split("\n").length))} className={inputCls} />
          : <input value={str} onChange={(e) => onChange(path, e.target.value)} className={inputCls} />}
      </label>
    );
  }
  if (typeof value === "number") {
    return (
      <label className="block" key={key}>
        <span className="text-neutral-500 text-[11px] uppercase tracking-wide">{label}</span>
        <input type="number" value={value} onChange={(e) => onChange(path, e.target.value === "" ? 0 : Number(e.target.value))} className={inputCls} />
      </label>
    );
  }
  if (typeof value === "boolean") {
    return (
      <label className="flex items-center gap-2 py-1" key={key}>
        <input type="checkbox" checked={value} onChange={(e) => onChange(path, e.target.checked)} className="accent-[#ff0033] w-4 h-4" />
        <span className="text-neutral-300 text-sm">{label}</span>
      </label>
    );
  }
  if (Array.isArray(value)) {
    const addItem = () => onChange(path, [...value, value.length ? emptyLike(value[value.length - 1]) : ""]);
    const removeItem = (i) => onChange(path, value.filter((_, idx) => idx !== i));
    return (
      <div className="border border-white/10 bg-[#0A0A0A] p-3" key={key}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[#ff0033] text-[11px] uppercase tracking-widest font-mono-lab">{label} · {value.length}</span>
          <button onClick={addItem} className="flex items-center gap-1 text-neutral-400 hover:text-[#ff0033] text-xs"><Plus size={13} /> Hinzufügen</button>
        </div>
        <div className="space-y-3">
          {value.map((item, i) => (
            <div key={i} className="relative border-l-2 border-white/10 pl-3">
              <button onClick={() => removeItem(i)} title="Eintrag entfernen" className="absolute -left-[1px] top-0 text-neutral-600 hover:text-[#ff0033] bg-[#0A0A0A]"><Trash2 size={13} /></button>
              <div className="pl-4">
                {renderField(`#${i + 1}`, item, [...path, i], onChange, depth + 1)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className={depth === 0 ? "" : "border border-white/10 bg-[#0A0A0A] p-3"} key={key}>
      {depth > 0 && <span className="text-neutral-500 text-[11px] uppercase tracking-widest font-mono-lab block mb-2">{label}</span>}
      <div className="grid grid-cols-1 gap-3">
        {Object.keys(value).map((k) => renderField(k, value[k], [...path, k], onChange, depth + 1))}
      </div>
    </div>
  );
};

const ContentEditor = ({ canReset }) => {
  const { reloadContent } = useLang();
  const [lang, setLang] = useState("de");
  const [data, setData] = useState(null);
  const [section, setSection] = useState("home");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const buildFrom = (overrides) => ({
    de: deepMerge(translations.de, overrides.de || {}),
    en: deepMerge(translations.en, overrides.en || {}),
  });

  const fetchContent = () => api.get("/content").then((r) => setData(buildFrom(r.data || {}))).catch(() => setData(buildFrom({})));

  useEffect(() => {
    fetchContent();
    const onVis = () => { if (document.visibilityState === "visible" && !dirty) fetchContent(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [dirty]); // eslint-disable-line

  const onChange = (path, value) => {
    setData((prev) => setPath(prev, path, value));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Nur tatsächlich geänderte Werte speichern (sparse) – so überschreiben alte
      // Speicherstände niemals spätere Standard-Texte oder neue Seiten-Inhalte.
      const overrides = {
        de: diffContent(translations.de, data.de) || {},
        en: diffContent(translations.en, data.en) || {},
      };
      await api.put("/admin/content", { data: overrides });
      await reloadContent();
      toast.success("Website-Inhalte gespeichert · sofort live");
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Wirklich ALLE Text-Änderungen auf die Standard-Texte zurücksetzen?")) return;
    try {
      await api.delete("/admin/content");
      setData(buildFrom({}));
      await reloadContent();
      setDirty(false);
      toast.success("Auf Standard-Texte zurückgesetzt");
    } catch { toast.error("Fehler beim Zurücksetzen"); }
  };

  if (!data) return <p className="text-neutral-500 font-mono-lab">Lädt…</p>;

  const allSections = Object.keys(translations.de);
  const sections = [
    ...SECTION_ORDER.filter((s) => allSections.includes(s)),
    ...allSections.filter((s) => !SECTION_ORDER.includes(s)),
  ];
  const current = data[lang][section];

  return (
    <div data-testid="content-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-4xl uppercase tracking-tight text-white">Website · Inhalte</h1>
          <p className="text-neutral-500 text-sm mt-1">Bearbeite jeden Text der Seite. Speichern wird sofort live übernommen.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-white/15" data-testid="content-lang-toggle">
            {["de", "en"].map((l) => (
              <button key={l} onClick={() => setLang(l)} className={`flex items-center gap-1.5 px-3 py-2 font-mono-lab text-xs uppercase tracking-widest ${lang === l ? "bg-[#ff0033] text-white" : "text-neutral-400"}`}>
                <Globe size={13} /> {l}
              </button>
            ))}
          </div>
          {canReset && <button onClick={reset} data-testid="content-reset-btn" className="flex items-center gap-1.5 border border-white/15 text-neutral-300 hover:text-[#ff0033] px-3 py-2 font-mono-lab text-xs uppercase tracking-widest"><RotateCcw size={14} /> Reset</button>}
          <button onClick={save} disabled={saving} data-testid="content-save-btn" className="flex items-center gap-2 bg-[#ff0033] text-white font-mono-lab text-xs uppercase tracking-widest px-4 py-2.5 disabled:opacity-50">
            <Save size={15} /> {saving ? "Speichert…" : dirty ? "Speichern *" : "Speichern"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        <nav className="space-y-1 lg:sticky lg:top-24 self-start" data-testid="content-sections">
          {sections.map((s) => (
            <button key={s} onClick={() => setSection(s)} data-testid={`content-section-${s}`}
              className={`w-full flex items-center justify-between text-left px-3 py-2.5 text-sm ${section === s ? "bg-[#ff0033] text-white" : "text-neutral-400 hover:text-white hover:bg-white/5"}`}>
              {labelFor(s, true)} {section === s ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          ))}
        </nav>

        <div className="min-w-0">
          <div className="bg-[#0F0F0F] border border-white/10 p-4 md:p-6">
            <p className="font-mono-lab text-xs tracking-widest text-[#ff0033] uppercase mb-4">{labelFor(section, true)} · {lang.toUpperCase()}</p>
            {renderField(section, current, [lang, section], onChange, 0)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentEditor;
